/* eslint-disable @typescript-eslint/naming-convention */

import { ContentType, DirectActionRecipientType, GameType, GenericSelectorTargets, InteractionType, ItemFunctionality, LocationType, NotificationType, PetUpdateType, QuestTaskStatusType, SignalType, UIContextAction } from "angular-to-phaser";
import { forkJoin, map, Observable, of, tap } from "rxjs";
import { InventoryService } from "./services/inventory.service";
import { Hydratable, JsonEnricher, JsonMappable } from "./services/jsonmapper.service";
import { PetService } from "./services/pet.service";
import { UnifiedRepositoryGatewayService } from "./services/repository/unified-repository-gateway.service";
import { UserService } from "./services/user.service";
import { Utils } from "./utils";


export class User implements JsonMappable<User> {
    repositoryLabel = 'User';
    isJsonMappableInstance = true;

    constructor(jsonShape: User = null) { this.map(jsonShape, null); }

    map(jsonShape: User, jsonMapper: JsonEnricher): User {
        if (!jsonShape) { return null; }

        this.Username = jsonShape.Username;
        this.FirstName = jsonShape.FirstName;
        this.LastName = jsonShape.LastName;
        this.EmailAddress = jsonShape.EmailAddress;
        this.Cash = jsonShape.Cash;

        this.Pronouns = jsonShape.Pronouns;
        this.JoinDate = new Date(jsonShape.JoinDate);
        this.Birthdate = Utils.toDateOnly(jsonShape.Birthdate);

        this.City = jsonShape.City;
        this.Country = jsonShape.Country;
        this.Password = jsonShape.Password;
        this.PronounSet = Pronouns.getPronouns(jsonShape.Pronouns);
        return this;
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) { /* nothing nested */ }

    public Username: string = null;
    public FirstName: string = null;
    public LastName: string = null;
    public EmailAddress: string = null;
    public Cash: number = null;

    /** Serverside int only, do not use unless you're hella smart (you aint) */
    public Pronouns: number = null;
    public JoinDate: Date = null;
    public Birthdate: Date = new Date();
    public City: string = null;
    public Country: string = null;
    public Password: string = null;

    public PronounSet: Pronouns;
}

export class Pronouns {
    public static THEYTHEM: Pronouns = {
        Id: 0,
        FullName: "they, them",
        Subject: "they",
        Object: "them"
    };
    public static SHEHER = {
        Id: 1,
        FullName: "she, her",
        Subject: "she",
        Object: "her"
    };
    public static HEHIM = {
        Id: 2,
        FullName: "he, him",
        Subject: "he",
        Object: "him"
    };

    /** Correlates to Server enum (User.Pronoun) */
    public Id: number;
    public FullName: string;
    public Subject: string;
    public Object: string;

    public static getPronouns(serverPronounsEnum: number) {
        switch (serverPronounsEnum) {
            case Pronouns.THEYTHEM.Id:
                return Pronouns.THEYTHEM;
            case Pronouns.SHEHER.Id:
                return Pronouns.SHEHER;
            case Pronouns.HEHIM.Id:
                return Pronouns.HEHIM;
            default: return null;
        }
    }

    public static getAll() {
        return [Pronouns.THEYTHEM, Pronouns.SHEHER, Pronouns.HEHIM];
    }
}

export class UserImage implements JsonMappable<UserImage> {
    repositoryLabel = 'UserImage';
    isJsonMappableInstance = true;

    public constructor() { }

    map(jsonShape: UserImage, jsonMapper: JsonEnricher): UserImage {
        if (!jsonShape) return null;

        this.Id = jsonShape.Id;
        this.Name = jsonShape.Name;
        this.Description = jsonShape.Description;
        this.ImagePath = `assets/images/users/${jsonShape.ImagePath}`;
        this.IsActive = jsonShape.IsActive;
        return this;
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) { /*nothing nested :) */ }


    public Id: string;
    public Name: string;
    public Description: string;
    public ImagePath: string;
    public IsActive: boolean;
}

// Owned-only cosmetic definition for ProfileBackground/PetBackground - a plain DTO, not
// JsonMappable/Hydratable: these are never returned as a partial {Id}-only flyweight, so
// there's no lazy-fill/caching machinery to earn its keep here.
export class CustomizationOption {
    public Id: string;
    public Type: number;
    public Name: string;
    public Description: string;
    public ImagePath: string;
    public IsActive: boolean;
}

export class UserProfile implements JsonMappable<UserProfile> {
    repositoryLabel = 'UserProfile';
    isJsonMappableInstance = true;

    public constructor() { }

    map(jsonShape: UserProfile, jsonMapper: JsonEnricher): UserProfile {
        if (!jsonShape) return null;

        this.Username = jsonShape.Username;
        this.Description = jsonShape.Description;
        this.Image = jsonMapper.map(jsonShape.Image, UserImage);
        this.ProfileBackground = jsonShape.ProfileBackground;
        this.LastLogin = new Date(jsonShape.LastLogin);

        return this;
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) {
        unifiedRepoGateway.cacheItems(UserImage, this.Image);
    }

    public Username: string;
    public Description: string;
    public LastLogin: Date;
    public Image: UserImage;
    public ProfileBackground: CustomizationOption;
}

export class CreateAccountRequest {
    public constructor(public User: User, public Pet: PetDTO, public PersonalityQuizAnswers: QuizAnswer[]) { }
}

export class AuthenticationResponse {
    public AuthToken: string;
    public SessionSummary: AuthenticatedSessionSummary;
}

export class AuthenticatedSessionSummary {
    public User: User;
    public TutorialComplete: boolean;
}

export class PetCreateRequest {
    public constructor(public Pet: PetDTO, public PersonalityQuizAnswers: QuizAnswer[]) { }
}

/** Player-facing shape - the client only ever knows a flag's id, its opaque CodeName, and its current value, never its (possibly sensitive) developer-facing name. */
export class FeatureFlagClientDTO {
    public Id: number;
    public CodeName: string;
    public Enabled: boolean;
}

export class QuizAnswer {
    public constructor(public QuestionId: string, public AnswerId: number) { }
}

export class PetColorConfig {
    public Id: number;
    public Name: string;
    public ImagePath: string;
    public static GetImagePath(psc: PetSpeciesConfig, pcc: PetColorConfig): string {
        return PetDTO.GetImagePath(psc, pcc, null);
    }
}

export class PetSpeciesConfig {
    public Id: number;
    public Name: string;
    public MaxHitPoints: number;
    public Description: string;
    public ImagePath: string;
}

export class PetDTO {
    public constructor(jsonShape: PetDTO) {
        if (!jsonShape) return null;

        this.Id = jsonShape.Id;
        this.Name = jsonShape.Name;
        this.Gender = jsonShape.Gender;
        this.SpeciesId = jsonShape.SpeciesId;
        this.ColorId = jsonShape.ColorId;
        this.IsAbandoned = jsonShape.IsAbandoned;
        this.Level = jsonShape.Level;
        this.HealthPoints = jsonShape.HealthPoints;
        this.BaseHealthPoints = jsonShape.BaseHealthPoints;
        this.FoodLevel = jsonShape.FoodLevel;
        this.Mood = jsonShape.Mood;
        this.MoodImagePath = jsonShape.MoodImagePath;
        this.Species = jsonShape.Species;
        this.Color = jsonShape.Color;
        this.Background = jsonShape.Background;

        this.ImagePath = PetDTO.GetImagePath(this.Species, this.Color, this.MoodImagePath);
    }

    public Id: number;
    public Name: string;
    public Gender: string;
    public SpeciesId: number;
    public ColorId: number;
    public IsAbandoned: boolean;
    public Level: number;
    public HealthPoints: number;
    public BaseHealthPoints: number;
    public FoodLevel: string;
    public Mood: string;
    public MoodImagePath: string;

    public Species: PetSpeciesConfig;
    public Color: PetColorConfig;
    public Background: CustomizationOption;

    public ImagePath: string;

    public UpdateMood(mood: string, moodImagePath: string) {
        this.Mood = mood;
        this.MoodImagePath = moodImagePath;
        this.ImagePath = PetDTO.GetImagePath(this.Species, this.Color, this.MoodImagePath);
    }

    public static GetImagePath(psc: PetSpeciesConfig, pcc: PetColorConfig, moodImagePath: string = 'happy'): string {
        if (pcc) {
            return `assets/images/pets/${psc.ImagePath}_${pcc.ImagePath}_${moodImagePath}.png`;
        } else {
            return `assets/images/pets/${psc.ImagePath}.png`;
        }
    }
}

export class SearchResult {
    constructor(pets: PetDTO[], users: User[]) {
        if (pets)
            this.Pets = pets.map(pet => new PetDTO(pet));
        if (users)
            this.Users = users.map(user => new User(user));
    }

    public Pets: PetDTO[] = [];
    public Users: User[] = [];
}

export class SearchResult2 {

    constructor(pet: PetDTO, user: User) {
        this.Pet = new PetDTO(pet);
        this.User = new User(user);
    }

    public Pet: PetDTO;
    public User: User;
}

export class NotificationTarget {
    public targetingNotification: Notification;
}

export class Friendship extends NotificationTarget implements Hydratable<Friendship> {

    repositoryLabel = 'Friendship';
    isJsonMappableInstance = true;
    isHydratableInstance = true;
    hasBeenRehydrated = false;

    public SenderUsername: string;
    public RecipientUsername: string;
    public Accepted: boolean;
    public SendDate: Date;
    public FriendUsername: string;

    //local hydrated
    public Profile: UserProfile;

    public constructor() { super(); }

    map(jsonShape: Friendship, jsonMapper: JsonEnricher): Friendship {
        if (!jsonShape) return null;

        this.Accepted = jsonShape.Accepted;
        this.FriendUsername = jsonShape.FriendUsername;
        this.SendDate = new Date(jsonShape.SendDate);
        this.RecipientUsername = jsonShape.RecipientUsername;
        this.SenderUsername = jsonShape.SenderUsername;

        //local only
        this.targetingNotification = null;
        this.Profile = jsonShape.Profile ? new UserProfile().map(jsonShape.Profile, jsonMapper) : null;
        return this;
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) {
        unifiedRepoGateway.cacheItems(UserProfile, this.Profile);
    }

    hydrate(jsonShape: Friendship, jsonMapper: JsonEnricher): Observable<Friendship> {
        this.map(jsonShape, jsonMapper);
        if (jsonShape.isHydratableInstance && jsonShape.Profile) {
            this.hasBeenRehydrated = true;
            return of(this);
        } else {
            return jsonMapper.getOrRetrieveByIds(true, true, [jsonShape.FriendUsername], UserProfile)
                .pipe(
                    tap(() => this.hasBeenRehydrated = true),
                    map(profileFetched => {
                        this.Profile = jsonMapper.map(profileFetched[0], UserProfile);
                        return this;
                    }));
        }

    }

    equals(note: Friendship): boolean {
        if (!note) return false;
        return note.FriendUsername.localeCompare(this.FriendUsername) == 0;
    }
}

export class MessageResponse {
    public Channels: Channel[];

    public constructor(jsonShape: MessageResponse, activeUsername: string) {
        if (!jsonShape || !activeUsername) return;

        this.Channels = jsonShape.Channels.map(ch => new Channel(ch, activeUsername))
    }
}

// The only definition of the text shown for a moderated message - the server never sends placeholder text.
export const MODERATED_MESSAGE_TEXT = '[This message was removed by moderation.]';

export class SendMessageResponse {
    public MessageId: number;
    public SendDate: Date;
    public MessageText: string;
    public Subject: string;
    public IsPartiallyBlocked: boolean;
    public OriginalMessageText: string;
    public OriginalSubject: string;
    public IsFullyBlocked: boolean;

    public constructor(jsonShape: SendMessageResponse) {
        if (!jsonShape) return;

        this.MessageId = jsonShape.MessageId
        this.SendDate = new Date(jsonShape.SendDate);
        this.MessageText = jsonShape.MessageText;
        this.Subject = jsonShape.Subject;
        this.IsPartiallyBlocked = !!jsonShape.IsPartiallyBlocked;
        this.OriginalMessageText = jsonShape.OriginalMessageText;
        this.OriginalSubject = jsonShape.OriginalSubject;
        this.IsFullyBlocked = !!jsonShape.IsFullyBlocked;
    }
}

export class Channel {
    constructor(jsonShape: Channel = null, myUsername: string) {
        if (!jsonShape) { return; }

        this.Id = jsonShape.Id;
        this.IsPublic = jsonShape.IsPublic;
        this.Name = jsonShape.Name;
        this.CreatorUsername = jsonShape.CreatorUsername;
        this.CreateDate = new Date(jsonShape.CreateDate);
        this.Usernames = jsonShape.Usernames;
        this.Messages = jsonShape?.Messages?.map(val => new Message(val, myUsername)) ?? [];
        this.MessageCount = jsonShape.MessageCount;
    }
    public Id: number;
    public IsPublic: boolean;
    public Name: string;
    public CreatorUsername: string;
    public CreateDate: Date;
    public Messages: Message[] = [];
    public Usernames: string[] = [];
    public MessageCount: number;

    //local
    public UserProfiles: UserProfile[] = [];
    public ContainsNotification: boolean = false;
}

export class Message extends NotificationTarget implements Hydratable<Message> {
    repositoryLabel = 'Message';
    isJsonMappableInstance = true;
    isHydratableInstance = true;
    hasBeenRehydrated = false;

    constructor(jsonShape: Message = null, myUsername: string = null) {
        super();
        if (!jsonShape) { return; }

        this.map(jsonShape, null);
        this.IsMyMessage = jsonShape.SenderUsername.localeCompare(myUsername) === 0;
    }

    map(jsonShape: Message, jsonMapper: JsonEnricher): Message {
        if (!jsonShape) return null;

        this.Id = jsonShape.Id;
        this.SenderUsername = jsonShape.SenderUsername;
        this.SendDate = new Date(jsonShape.SendDate);
        this.MessageText = jsonShape.MessageText;
        this.Subject = jsonShape.Subject;
        this.IsPartiallyBlocked = !!jsonShape.IsPartiallyBlocked;
        this.OriginalMessageText = jsonShape.OriginalMessageText;
        this.OriginalSubject = jsonShape.OriginalSubject;
        this.IsFullyBlocked = !!jsonShape.IsFullyBlocked;
        this.ParentMessageId = jsonShape.ParentMessageId;
        this.ChannelId = jsonShape.ChannelId;
        this.Read = jsonShape.Read;

        this.targetingNotification = null;
        this.IsMyMessage = jsonShape.IsMyMessage;
        this.SenderProfile = jsonShape.SenderProfile;
        this.hasBeenRehydrated = !!this.SenderProfile;
        return this;
    }

    // The server never sends SenderProfile (see field comment below) - a message is only ever
    // "rehydrated" by fetching its sender's profile client-side. Mirrors Friendship.hydrate()'s
    // pattern: reuse an already-hydrated Profile if this instance already carries one, otherwise
    // fetch by username through the unified repository gateway (cache-first, so any other message
    // from the same sender already in memory resolves for free).
    hydrate(jsonShape: Message, jsonMapper: JsonEnricher): Observable<Message> {
        this.map(jsonShape, jsonMapper);
        if (jsonShape.isHydratableInstance && jsonShape.SenderProfile) {
            this.hasBeenRehydrated = true;
            return of(this);
        } else {
            return jsonMapper.getOrRetrieveByIds(true, true, [jsonShape.SenderUsername], UserProfile)
                .pipe(
                    tap(() => this.hasBeenRehydrated = true),
                    map(profileFetched => {
                        this.SenderProfile = jsonMapper.map(profileFetched[0], UserProfile);
                        return this;
                    }));
        }
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) {
        unifiedRepoGateway.cacheItems(UserProfile, this.SenderProfile);
    }

    public Id: number;
    public SenderUsername: string;
    public SendDate: Date;
    public MessageText: string;
    public Subject: string;
    public ParentMessageId: number;
    public ChannelId: number;
    public Read: boolean;

    // Moderation state set by the server. A blocked message arrives with null MessageText/Subject:
    // a partially blocked one also carries the real content in Original*, a fully blocked one
    // carries nothing. Original* only mirror the wire shape - every renderer must go through
    // DisplayText/DisplaySubject (or app-message-body) and never read them.
    public IsPartiallyBlocked: boolean = false;
    public OriginalMessageText: string;
    public OriginalSubject: string;
    public IsFullyBlocked: boolean = false;

    get IsModerated(): boolean {
        return this.IsPartiallyBlocked || this.IsFullyBlocked;
    }

    // The body and subject text safe to show anywhere a plain string is needed (previews, toasts).
    get DisplayText(): string {
        return this.IsModerated ? MODERATED_MESSAGE_TEXT : this.MessageText;
    }

    get DisplaySubject(): string {
        return this.IsModerated ? MODERATED_MESSAGE_TEXT : this.Subject;
    }

    //local hydrated
    public IsMyMessage: boolean;
    public SenderProfile: UserProfile;
}


export class ItemTypeConfig implements JsonMappable<ItemTypeConfig> {
    repositoryLabel = 'ItemTypeConfig';
    isJsonMappableInstance = true;

    public constructor() { }

    map(jsonShape: ItemTypeConfig, jsonMapper: JsonEnricher) {
        if (!jsonShape) return null;
        this.Id = jsonShape.Id;
        this.Name = jsonShape.Name;
        this.Description = jsonShape.Description;
        if (jsonShape.ImagePath) {
            this.ImagePath = jsonShape.ImagePath.includes('assets/images/items') ? jsonShape.ImagePath : `assets/images/items/${jsonShape.ImagePath}`;
        }
        if (this.hasDefinitionDetails()) {
            jsonMapper?.repositoryService.cacheItems(ItemTypeConfig, this);
        }
        return this;
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) { /* nothing nested */ }

    public hasDefinitionDetails(): boolean {
        return !!(this.Name && this.ImagePath);
    }

    public Id: number;
    public Name: string;
    public Description: string;
    public ImagePath: string;
}

export class InventoryItem implements Hydratable<InventoryItem> {
    repositoryLabel = 'InventoryItem';
    isJsonMappableInstance = true;
    isHydratableInstance = true;
    hasBeenRehydrated = false;

    constructor() { }

    hydrate(jsonShape: InventoryItem, jsonMapper: JsonEnricher): Observable<InventoryItem> {
        this.map(jsonShape, jsonMapper);
        if (this.ItemType) {
            if (this.hasFullItemTypeDefinition()) {
                this.hasBeenRehydrated = true;
                return of(this);
            } else {
                return jsonMapper.retrieveItemWithId(true, true, this.ItemType.Id, ItemTypeConfig)
                    .pipe(map(itemTypeHydrated => {
                        this.ItemType = itemTypeHydrated[0];
                        this.hasBeenRehydrated = this.hasFullItemTypeDefinition();
                        return this;
                    }))
            }
        }
        return of(this);
    }

    map(jsonShape: InventoryItem, jsonMapper: JsonEnricher): InventoryItem {
        if (!jsonShape) return null;
        this.Id = jsonShape.Id;
        this.ItemType = jsonMapper.map(jsonShape.ItemType, ItemTypeConfig);
        this.hasBeenRehydrated = this.hasFullItemTypeDefinition();
        return this;
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) {
        unifiedRepoGateway.cacheItems(ItemTypeConfig, this.ItemType);
    }

    public Id: number;
    public ItemType: ItemTypeConfig;

    private hasFullItemTypeDefinition(): boolean {
        return this.ItemType?.isJsonMappableInstance && this.ItemType.hasDefinitionDetails();
    }
}
export class ItemUse {
    public ItemFunctionality: ItemFunctionality;
    public ItemTypeId: number;
    public Text: string;
}

export class PetResult {
    public PetId: number;


    public ChangedStat: PetUpdateType;

    public InitialState: string;
    public InitialStateValue: number;
    public FinalState: string;
    public FinalStateValue: number;
    public FinalStateMoodImagePath: string

}


export class InventoryResponse implements JsonMappable<InventoryResponse> {
    repositoryLabel = 'InventoryResponse';
    isJsonMappableInstance = true;

    constructor() { }

    map(jsonShape: InventoryResponse, jsonMapper: JsonEnricher) {
        if (!jsonShape) return null;
        if (jsonShape.Types)
            this.Types = jsonShape.Types.map(t => jsonMapper.map(t, ItemTypeConfig));
        if (jsonShape.Items)
            this.Items = jsonShape.Items.map(i => jsonMapper.map(i, InventoryItem));
        return this;
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) {
        unifiedRepoGateway.cacheItems(ItemTypeConfig, ...this.Types);
        unifiedRepoGateway.cacheItems(InventoryItem, ...this.Items)
    }

    public Items: InventoryItem[] = [];
    public Types: ItemTypeConfig[] = [];
}



export class Visitable {
    public constructor(jsonShape: Visitable) {
        if (!jsonShape) return;
        this.Name = jsonShape.Name;
        this.Description = jsonShape.Description;
        this.ImagePath = jsonShape.ImagePath;
        this.DisplaySortOrder = jsonShape.DisplaySortOrder;
        this.OutsideName = jsonShape.OutsideName;
        this.OutsideImagePath = jsonShape.OutsideImagePath;
        this.BackgroundImagePath = jsonShape.BackgroundImagePath;
        this.CssClasses = jsonShape.CssClasses;
        this.CustomCss = jsonShape.CustomCss;
        this.PrecontentCssClasses = jsonShape.PrecontentCssClasses;
        this.PrecontentCustomCss = jsonShape.PrecontentCustomCss;
        if (jsonShape.ParentZone) {
            this.ParentZone = new Zone(jsonShape.ParentZone);
        }
    }
    public Name: string;
    public Description: string;
    public ImagePath: string;
    public DisplaySortOrder: number;
    public LocationType: LocationType;
    public OutsideName: string;
    public OutsideImagePath: string;
    public BackgroundImagePath: string;
    public ParentZone: Zone;
    public CustomCss: string;
    public CssClasses: string[];
    public PrecontentCustomCss: string;
    public PrecontentCssClasses: string[];
}

export class Shop extends Visitable {
    public constructor(jsonShape: Shop) {
        super(jsonShape);
        this.LocationType = LocationType.Shop;
        if (!jsonShape) return;

        this.Id = jsonShape.Id;
        this.OwnerUsername = jsonShape.OwnerUsername;
        if (jsonShape.OwnerNpc) {
            this.OwnerNpc = new Npc(jsonShape.OwnerNpc);
        }

        if (jsonShape.BackgroundImagePath) {
            this.BackgroundImagePath = jsonShape.BackgroundImagePath.includes('assets/images/shops') ? jsonShape.BackgroundImagePath : `assets/images/shops/${jsonShape.BackgroundImagePath}`;
        }
        if (jsonShape.ImagePath) {
            this.ImagePath = jsonShape.ImagePath.includes('assets/images/shops') ? jsonShape.ImagePath : `assets/images/shops/${jsonShape.ImagePath}`;
        }
        if (jsonShape.OutsideImagePath) {
            this.OutsideImagePath = jsonShape.OutsideImagePath.includes('assets/images/shops') ? jsonShape.OutsideImagePath : `assets/images/shops/${jsonShape.OutsideImagePath}`;
        }
    }

    public Id: number;
    public OwnerUsername: string;
    public OwnerNpc: Npc;

    public Items: ShopItem[] = [];//client side only
}

export class ShopItem extends InventoryItem implements JsonMappable<ShopItem> {
    public ShopId: number;
    public Price: number;
    public ShopDescription: string;

    constructor() { super(); }


    map(jsonShape: ShopItem, jsonMapper: JsonEnricher) {
        if (!jsonShape) return null;

        this.ShopId = jsonShape.ShopId;
        this.ShopDescription = jsonShape.ShopDescription;
        this.Price = jsonShape.Price;
        super.map(jsonShape, jsonMapper);
        return this;
    }

}

export class ShopStockResponse implements JsonMappable<ShopStockResponse> {
    repositoryLabel = 'ShopStockResponse';
    isJsonMappableInstance = true;

    public constructor() { }

    map(jsonShape: ShopStockResponse, jsonMapper: JsonEnricher) {
        if (!jsonShape) return null;


        if (jsonShape.Types)
            this.Types = jsonShape.Types.map(t => jsonMapper.map(t, ItemTypeConfig));

        if (jsonShape.Items)
            this.Items = jsonShape.Items.map(i => jsonMapper.map(i, ShopItem));
        return this;
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) {
        unifiedRepoGateway.cacheItems(ShopItem, ...this.Items);
        unifiedRepoGateway.cacheItems(ItemTypeConfig, ...this.Types);
    }

    public Items: ShopItem[] = [];
    public Types: ItemTypeConfig[] = [];
}

export class Zone extends Visitable {
    public constructor(jsonShape: Zone) {
        super(jsonShape);
        this.LocationType = LocationType.Zone;
        this.Interactive = jsonShape.Interactive ?? false
        if (!jsonShape) return;

        this.ExternalId = jsonShape.ExternalId;
        this.OwnerNpcExternalId = jsonShape.OwnerNpcExternalId;
        if (jsonShape.Npcs)
            this.Npcs = jsonShape.Npcs.map(npc => new Npc(npc)).sort(n => n.DisplaySortOrder);
        if (jsonShape.Zones)
            this.Zones = jsonShape.Zones.map(zone => new Zone(zone)).sort(n => n.DisplaySortOrder);
        if (jsonShape.Shops)
            this.Shops = jsonShape.Shops.map(shop => new Shop(shop));
        this.AllLocations = (<Visitable[]>this.Zones).concat(...this.Shops).sort(l => l.DisplaySortOrder);

        let imagePath = jsonShape.OwnerNpcExternalId ? Npc.IMAGE_PATH : Zone.IMAGE_PATH; //used for zones like the World Pizza Organization, who behave like/are also NPCs
        if (jsonShape.BackgroundImagePath) {
            this.BackgroundImagePath = jsonShape.BackgroundImagePath.includes(imagePath) ? jsonShape.BackgroundImagePath : `${imagePath}/${jsonShape.BackgroundImagePath}`;
        }
        if (jsonShape.ImagePath) {
            this.ImagePath = jsonShape.ImagePath.includes(imagePath) ? jsonShape.ImagePath : `${imagePath}/${jsonShape.ImagePath}`;
        }
        if (jsonShape.OutsideImagePath) {
            this.OutsideImagePath = jsonShape.OutsideImagePath.includes(imagePath) ? jsonShape.OutsideImagePath : `${imagePath}/${jsonShape.OutsideImagePath}`;
        }
    }

    public static IMAGE_PATH = 'assets/images/zones';

    public ExternalId: string;
    public OwnerNpcExternalId: string;
    public Interactive: boolean;
    public Npcs: Npc[] = [];
    public Zones: Zone[] = [];
    public Shops: Shop[] = [];
    public AllLocations: Visitable[] = [];
}

export class Npc extends Visitable {
    public constructor(jsonShape: Npc) {
        super(jsonShape);
        this.LocationType = LocationType.Npc;
        if (!jsonShape) return;
        this.ExternalId = jsonShape.ExternalId;

        if (jsonShape.BackgroundImagePath) {
            this.BackgroundImagePath = jsonShape.BackgroundImagePath.includes(Npc.IMAGE_PATH) ? jsonShape.BackgroundImagePath : `${Npc.IMAGE_PATH}/${jsonShape.BackgroundImagePath}`;
        }
        if (jsonShape.ImagePath) {
            this.ImagePath = jsonShape.ImagePath.includes(Npc.IMAGE_PATH) ? jsonShape.ImagePath : `${Npc.IMAGE_PATH}/${jsonShape.ImagePath}`;
        }
        if (jsonShape.OutsideImagePath) {
            this.OutsideImagePath = jsonShape.OutsideImagePath.includes(Npc.IMAGE_PATH) ? jsonShape.OutsideImagePath : `${Npc.IMAGE_PATH}/${jsonShape.OutsideImagePath}`;
        }
    }
    public static IMAGE_PATH = 'assets/images/npcs';


    public ExternalId: string;
}

export class DisplayElement {
    public constructor(jsonShape: DisplayElement) {
        if (!jsonShape) return;
        this.CustomCss = jsonShape.CustomCss;
        this.DisplayMetadata = jsonShape.DisplayMetadata;
        this.ContextSpecificData = jsonShape.ContextSpecificData
        this.LottieId = jsonShape.LottieId;
    }
    public CustomCss: string;
    public DisplayMetadata: DisplayMetadata;
    public ContextSpecificData: any;
    public LottieId: string;
}


export class GameStateResponse extends DisplayElement {
    public constructor(jsonShape: GameStateResponse, jsonMapper: JsonEnricher) {
        super(jsonShape)
        if (!jsonShape) return;
        this.Messages = jsonShape.Messages;
        this.PetChanges = jsonShape.PetChanges;
        this.PlayerStateUpdate = new PlayerStateUpdate(jsonShape.PlayerStateUpdate, jsonMapper);
        this.BackgroundPlayerStateUpdate = new PlayerStateUpdate(jsonShape.BackgroundPlayerStateUpdate, jsonMapper);

    }
    public Messages: string[];
    public PetChanges: PetResult[];
    public PlayerStateUpdate: PlayerStateUpdate;
    public BackgroundPlayerStateUpdate: PlayerStateUpdate;


    /**TODO consider replacing with observable or other event, listened to in all services */
    public static distributeGameStateToServices(gameStateUpdate: GameStateResponse, inventoryService: InventoryService, userService: UserService, petService: PetService): void {
        PlayerStateUpdate.distributeToServices(gameStateUpdate.PlayerStateUpdate, inventoryService, userService);
        PlayerStateUpdate.distributeToServices(gameStateUpdate.BackgroundPlayerStateUpdate, inventoryService, userService);

        if (gameStateUpdate.PetChanges) {
            gameStateUpdate.PetChanges?.forEach(pc => {
                petService.updatePet(pc);
            });
        }
    }

}


// Discriminated content-node vocabulary shipped by Interaction.Content (and nested inside
// TitleNode.Text). Mirrors CritterServer.Contract/Interactions/Node.cs — same NodeType
// discriminator convention as InteractionType/QuestTaskStatusType elsewhere in this file.
export enum NodeType {
    PlainText,
    Title,
    Image,
    TaskProgress
}

export abstract class Node {
    public NodeType: NodeType;
}

// Marker base for text-shaped content. Only PlainTextNode implements it today; a future
// MarkdownNode or styled-text variant slots in as a sibling with no change to anything
// that references TextNode (e.g. TitleNode.Text).
export abstract class TextNode extends Node {
}

export class PlainTextNode extends TextNode {
    public constructor(jsonShape: PlainTextNode) {
        super();
        this.NodeType = NodeType.PlainText;
        this.Text = jsonShape.Text;
    }
    public Text: string;
}

export class TitleNode extends Node {
    public constructor(jsonShape: TitleNode, jsonMapper: JsonEnricher) {
        super();
        this.NodeType = NodeType.Title;
        this.Text = <TextNode>hydrateNode(jsonShape.Text, jsonMapper);
    }
    public Text: TextNode;
}

export class ImageNode extends Node {
    public constructor(jsonShape: ImageNode) {
        super();
        this.NodeType = NodeType.Image;
        this.ImagePath = jsonShape.ImagePath;
    }
    public ImagePath: string;
}

// Task-status rendering folds into Content via this leaf — node-renderer's registry
// delegates it straight into the existing, unmodified app-quest-task-renderer registry.
export class TaskProgressNode extends Node {
    public constructor(jsonShape: TaskProgressNode, jsonMapper: JsonEnricher) {
        super();
        this.NodeType = NodeType.TaskProgress;
        this.Task = hydrateQuestTaskStatus(jsonShape.Task, jsonMapper);
    }
    public Task: QuestTaskStatus;
}

// Shared discriminator dispatch, reused both for Interaction.Content entries and for
// TitleNode.Text (a nested node, not a raw string — see TitleNode above).
export function hydrateNode(jsonShape: Node, jsonMapper: JsonEnricher): Node {
    if (!jsonShape) return null;
    switch (jsonShape.NodeType) {
        case NodeType.PlainText: return new PlainTextNode(<PlainTextNode>jsonShape);
        case NodeType.Title: return new TitleNode(<TitleNode>jsonShape, jsonMapper);
        case NodeType.Image: return new ImageNode(<ImageNode>jsonShape);
        case NodeType.TaskProgress: return new TaskProgressNode(<TaskProgressNode>jsonShape, jsonMapper);
        default: return null;
    }
}

// Single-string readout of a Content array — used wherever a plain string is needed (a
// modal title, an accordion header) instead of the full structured Content, e.g. in place
// of the old `interaction.Title ?? interaction.Message`. Content's synthesis order
// (Image, then Title, then plain-text Message, then anything appended) already puts Title
// ahead of Message, so returning the first Title/PlainText node's text reproduces that
// same "Title ?? Message" fallback with no special-casing needed.
export function firstText(content: Node[]): string {
    if (!content) return null;
    for (const node of content) {
        if (node.NodeType === NodeType.Title) {
            const inner = (<TitleNode>node).Text;
            if (inner instanceof PlainTextNode && inner.Text) return inner.Text;
        } else if (node.NodeType === NodeType.PlainText) {
            const text = (<PlainTextNode>node).Text;
            if (text) return text;
        }
    }
    return null;
}

// Replaces a token in-place across every text-bearing node in a Content array (PlainTextNode.Text,
// TitleNode.Text.Text) — the Content-based counterpart to the old pattern of mutating
// interaction.Message directly before hydration (Content didn't exist as a wire field back then).
export function replaceTokenInContent(content: Node[], token: string, value: string): void {
    if (!content) return;
    for (const node of content) {
        if (node.NodeType === NodeType.PlainText) {
            const plain = <PlainTextNode>node;
            if (plain.Text) plain.Text = plain.Text.replace(token, value);
        } else if (node.NodeType === NodeType.Title) {
            const inner = (<TitleNode>node).Text;
            if (inner instanceof PlainTextNode && inner.Text) inner.Text = inner.Text.replace(token, value);
        }
    }
}

export enum RefreshTargetKind {
    Npc,
    Zone
}

// Mirrors CritterServer.Contract.Interactions.RefreshTarget - a flat DTO with no nested
// hydration needed, so Interaction just copies it wholesale off the wire shape.
export class RefreshTarget {
    public Kind: RefreshTargetKind;
    public ExternalId: string;
}

export class Interaction extends GameStateResponse {
    public constructor(jsonShape: Interaction, jsonMapper: JsonEnricher) {
        super(jsonShape, jsonMapper);
        if (!jsonShape) return;
        this.Id = jsonShape.Id;
        this.InteractionType = jsonShape.InteractionType ?? InteractionType.Interaction;
        this.Responses = jsonShape.Responses;
        this.RefreshTarget = jsonShape.RefreshTarget;

        if (jsonShape.Content)
            this.Content = jsonShape.Content.map(n => hydrateNode(n, jsonMapper));

        if (jsonShape.Quests)
            this.Quests = jsonShape.Quests.map(q => new QuestStatus(q, jsonMapper));

        if (jsonShape.Responses)
            this.Responses = jsonShape.Responses.map(r => new InteractionReply(r, jsonMapper));
    }

    public interactorDisabled: boolean = false;

    public Id: number;
    public InteractionType: InteractionType;
    public Content: Node[] = [];
    public Quests: QuestStatus[];
    public Responses: InteractionReply[];

    // What to re-greet once a detached modal/minigame this Interaction opened closes - see
    // Docs/CritterClient/Interaction-Content-Rendering.md's refresh-target section. Null for a
    // non-dialogue Interaction (ordinary NPC/Zone content, QuestStatus).
    public RefreshTarget: RefreshTarget;

    public removeFromNestedQuests(find: Interaction): boolean {
        if (!this.Quests || !this.Quests.length) return false;

        let index = this.Quests.indexOf(<QuestStatus>find); //see if find IS A nested quest
        if (index >= 0) {
            this.Quests.splice(index, 1);
            return true;
        }

        index = this.Quests.findIndex(nestedQuest => nestedQuest.removeFromNestedQuests(find) || nestedQuest.removeFromNestedResponses(find));

        if (index >= 0) {
            this.Quests.splice(index, 1);
            return true;
        }

        return false;
    }

    public replaceInNestedQuests(find: Interaction, replacement: Interaction): boolean {
        if (!this.Quests || !this.Quests.length) return false;

        let index = this.Quests.indexOf(<QuestStatus>find); //see if find IS A nested quest
        if (index >= 0) {
            this.Quests[index] = <QuestStatus>replacement;
            return true;
        }

        index = this.Quests.findIndex(nestedQuest => nestedQuest.removeFromNestedQuests(find) || nestedQuest.removeFromNestedResponses(find));


        if (index >= 0) {
            this.Quests[index] = <QuestStatus>replacement;
            return true;
        }

        return false;
    }

    public removeFromNestedResponses(find: Interaction): boolean {
        if (!this.Responses || !this.Responses.length) return false;

        let index = this.Responses.findIndex(r => r.hasNextEventInteraction(find)); //see if find IS A Response.Action.NextEvent
        if (index >= 0) {
            this.Responses.splice(index, 1);
            return true;
        }

        return false;
    }

}


// Collapses the old QuestPreview/QuestStatus pair into one type, mirroring
// CritterServer.Contract.Quests.QuestStatus — a QuestStatus IS the preview
// (Interaction.Quests entries), and its NextEvent-linked Responses point at further
// QuestStatus instances built server-side via BuildDetail(...).
export class QuestStatus extends Interaction {
    // Task rendering happens exclusively via TaskProgressNodes inside Content, hydrated
    // by the base Interaction constructor above — no QuestTasks/QuestItemDefinitions
    // pooling here anymore. Each PossessionTaskStatus applies its own item-type
    // definitions during hydration (see PossessionTaskStatus.map()).
    public constructor(jsonShape: QuestStatus, jsonMapper: JsonEnricher) {
        super(jsonShape, jsonMapper);
        this.InteractionType = InteractionType.Quest;
        this.Completed = jsonShape.Completed;
        this.Accepted = jsonShape.Accepted;
    }

    public Completed: boolean;
    public Accepted: boolean;

    public hasResponseInteraction(find: Interaction): boolean {
        if (!this.Responses || !this.Responses.length) return false;
        return this.Responses.findIndex(response => response.Actions.findIndex(a => a.NextEvent == find)) >= 0;
    }
}
export abstract class QuestTaskStatus {//} implements JsonMappable<QuestTaskStatus> {
    public constructor() {//jsonShape: QuestTaskStatus){//}, jsonMapper: JsonMapperService) {
        // if (!jsonShape) return null;

        // this.Completable = this.Completable;
    }
    public QuestTaskStatusType: QuestTaskStatusType
    public Completable: boolean;
}

// Shared discriminator dispatch for a raw QuestTaskStatus — used by TaskProgressNode
// (see Node.ts above), the one place a QuestTaskStatus now arrives on the wire.
export function hydrateQuestTaskStatus(jsonShape: QuestTaskStatus, jsonMapper: JsonEnricher): QuestTaskStatus {
    if (!jsonShape) return null;
    switch (jsonShape.QuestTaskStatusType) {
        case QuestTaskStatusType.NpcStateTaskStatus: return jsonMapper.map(jsonShape, NpcStateTaskStatus);
        case QuestTaskStatusType.PossessionTaskStatus: return jsonMapper.map(jsonShape, PossessionTaskStatus);
        case QuestTaskStatusType.CashBalanceTaskStatus: return jsonMapper.map(jsonShape, CashBalanceTaskStatus);
        default: return null;
    }
}

export class NpcStateTaskStatus extends QuestTaskStatus implements JsonMappable<NpcStateTaskStatus> {
    repositoryLabel = 'NpcStateTaskStatus';
    isJsonMappableInstance = true;
    public constructor() { super(); }

    map(jsonShape: NpcStateTaskStatus, jsonMapper: JsonEnricher) {
        if (!jsonShape)
            return null;
        this.Completable = this.Completable;
        this.RequirementsCompleted = jsonShape.RequirementsCompleted;
        this.Description = jsonShape.Description;
        this.QuestTaskStatusType = QuestTaskStatusType.NpcStateTaskStatus;
        return this;
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) { /* no nests*/ }

    public RequirementsCompleted: NpcStateStatus[]
    public Description: string;
}

export class NpcStateStatus {
    public Description: string;
    public Completed: boolean;
}

export class PossessionTaskStatus extends QuestTaskStatus implements Hydratable<PossessionTaskStatus> {
    repositoryLabel = 'PossessionTaskStatus';
    isJsonMappableInstance = true;
    isHydratableInstance = true;
    hasBeenRehydrated = false;

    public constructor() { super(); }

    hydrate(jsonShape: PossessionTaskStatus, jsonMapper: JsonEnricher): Observable<PossessionTaskStatus> {
        this.map(jsonShape, jsonMapper);

        const requirementStatuses = this.ItemRequirementStatuses ?? [];
        if (this.hasBeenRehydrated && !requirementStatuses.find(irs => !irs.hasBeenRehydrated)) {
            return of(this);
        } else if (requirementStatuses.find(irs => !irs.hasBeenRehydrated)) {
            return forkJoin(requirementStatuses
                .filter(irs => !irs.hasBeenRehydrated)
                .map(irs => jsonMapper.hydrate(irs, ItemRequirementStatus)))
                .pipe(
                    map(() => {
                        this.hasBeenRehydrated = !this.ItemRequirementStatuses.find(irs => !irs.hasBeenRehydrated);
                        return this;
                    }));
        }
        return of(this);
    }

    map(jsonShape: PossessionTaskStatus, jsonMapper: JsonEnricher) {
        if (!jsonShape)
            return null;

        this.Completable = this.Completable;
        if (jsonShape.ItemRequirementStatuses)
            this.ItemRequirementStatuses = jsonShape.ItemRequirementStatuses.map(irs => jsonMapper.map(irs, ItemRequirementStatus));
        this.QuestTaskStatusType = QuestTaskStatusType.PossessionTaskStatus

        // Self-contained per-task definitions (not pooled at the top level on QuestStatus
        // anymore) — cross-reference against this task's own ItemRequirementStatuses.
        if (jsonShape.ItemTypeDefinitions) {
            this.ItemTypeDefinitions = {};
            Object.entries(jsonShape.ItemTypeDefinitions).forEach(([itemType, rawDefinition]) => {
                this.ItemTypeDefinitions[itemType] = jsonMapper.map(rawDefinition, ItemTypeConfig);
            });
            this.ItemRequirementStatuses?.forEach(requirement => {
                const definition = this.ItemTypeDefinitions[requirement.ItemType];
                if (definition) requirement.applyItemTypeDefinition(definition);
            });
        }

        this.hasBeenRehydrated = !!this.ItemRequirementStatuses?.length && !this.ItemRequirementStatuses.find(irs => !irs.hasBeenRehydrated);
        return this;
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) {
        this.ItemRequirementStatuses.forEach(status => status.cacheNestedElements(unifiedRepoGateway));
    }

    public ItemRequirementStatuses: ItemRequirementStatus[];
    public ItemTypeDefinitions: { [itemType: number]: ItemTypeConfig };
}

export class ItemRequirementStatus implements Hydratable<ItemRequirementStatus> {
    repositoryLabel = 'ItemRequirementStatus';
    isJsonMappableInstance = true;
    isHydratableInstance = true;
    hasBeenRehydrated = false;

    public ItemType: number;
    public RequiredCount: number;
    public Count: number;
    public RequirementSatisfiers: InventoryItem[];
    public CriterionSatisfiers: InventoryItem[];

    //enriched locally
    public itemTypeDefinition: ItemTypeConfig;
    public AsItem: InventoryItem;

    public constructor() { }

    map(jsonShape: ItemRequirementStatus, jsonMapper: JsonEnricher) {
        if (!jsonShape)
            return null;

        this.ItemType = jsonShape.ItemType;
        this.RequiredCount = jsonShape.RequiredCount;
        this.Count = jsonShape.Count;
        const itemTypeDefinition = jsonShape.itemTypeDefinition ?? (<any>jsonShape).ItemTypeDefinition;
        if (itemTypeDefinition) {
            this.applyItemTypeDefinition(itemTypeDefinition?.isJsonMappableInstance ? itemTypeDefinition : jsonMapper.map(itemTypeDefinition, ItemTypeConfig));
        }

        const requirementSatisfiers = jsonShape.RequirementSatisfiers ?? jsonShape.CriterionSatisfiers;
        if (requirementSatisfiers) {
            this.RequirementSatisfiers = requirementSatisfiers.map(item => jsonMapper.map(item, InventoryItem));
            this.CriterionSatisfiers = this.RequirementSatisfiers;
        }

        this.updateAsItem();
        return this;
    }

    hydrate(jsonShape: ItemRequirementStatus, jsonMapper: JsonEnricher): Observable<ItemRequirementStatus> {
        this.map(jsonShape, jsonMapper);

        if (this.hasBeenRehydrated && this.itemTypeDefinition?.isJsonMappableInstance) {
            return of(this);
        } else {
            return jsonMapper.getOrRetrieveByIds(true, true, [this.ItemType], ItemTypeConfig)
                .pipe(
                    map(itc => {
                        this.applyItemTypeDefinition(itc[0]);

                        return this;
                    })
                );
        }
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) {
        unifiedRepoGateway.cacheItems(ItemTypeConfig, this.itemTypeDefinition);
    }

    public applyItemTypeDefinition(itemTypeDefinition: ItemTypeConfig): void {
        this.itemTypeDefinition = itemTypeDefinition;
        this.hasBeenRehydrated = !!this.itemTypeDefinition?.hasDefinitionDetails();
        this.updateAsItem();
    }

    private updateAsItem(): void {
        this.AsItem = <any>{ isHydratableInstance: false, isJsonMappableInstance: false, hasBeenRehydrated: this.hasBeenRehydrated, ItemType: this.itemTypeDefinition, Id: -1 };
    }

}

export class CashBalanceTaskStatus extends QuestTaskStatus implements JsonMappable<CashBalanceTaskStatus> {
    public constructor() { super(); }
    repositoryLabel = 'CashBalanceTaskStatus';
    isJsonMappableInstance = true;


    map(jsonShape: CashBalanceTaskStatus, jsonMapper: JsonEnricher) {
        if (!jsonShape)
            return null;
        this.CurrentCashBalance = jsonShape.CurrentCashBalance;
        this.RequiredCashBalance = jsonShape.RequiredCashBalance;
        this.QuestTaskStatusType = QuestTaskStatusType.CashBalanceTaskStatus;
        this.Completable = this.Completable;
        return this;
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) { /* nothing nested */ }

    public CurrentCashBalance: number;
    public RequiredCashBalance: number;
}

export class InteractionReply extends DisplayElement {
    public constructor(jsonShape: InteractionReply, jsonMapper: JsonEnricher) {
        super(jsonShape);
        if (!jsonShape) return;


        this.OptionText = jsonShape.OptionText;
        this.UIContextAction = jsonShape.UIContextAction;
        this.ReturnsToEntry = jsonShape.ReturnsToEntry;

        if (jsonShape.Actions)
            this.Actions = jsonShape.Actions.map(a => hydrateReplyAction(a, jsonMapper));

    }

    public hasNextEventInteraction(find: Interaction): boolean {
        if (!this.Actions || !this.Actions.length) return false;

        if (this.Actions.findIndex(a => a.NextEvent == find) >= 0)
            return true;

        return this.Actions.findIndex(act => act.NextEvent && (act.NextEvent.removeFromNestedQuests(find) || act.NextEvent.removeFromNestedResponses(find))) >= 0;
    }

    public OptionText: string;
    public Actions: ReplyAction[];
    public UIContextAction: UIContextAction;
    public ReturnsToEntry: boolean;
}

// Discriminated union mirroring CritterServer.Contract.Interactions.ReplyAction — same
// closed-enum-discriminator convention as NodeType. RouteLink/ServerComponentLink fold
// their link shape directly into the reply subclass rather than nesting a second
// discrimination layer; a reply's ReplyActionType already says unambiguously whether it's
// a link, so link-vs-button rendering derives from that instead of a separately-tracked
// DisplayMetadata.IsLink bool.
export enum ReplyActionType {
    DirectAction,
    RouteLink,
    ServerComponentLink,
    ChainedInteraction
}

export abstract class ReplyAction {
    public ReplyActionType: ReplyActionType;
    public NextEvent: Interaction;
}

export class DirectActionReply extends ReplyAction {
    public constructor(jsonShape: DirectActionReply) {
        super();
        this.ReplyActionType = ReplyActionType.DirectAction;
        this.DirectAction = jsonShape.DirectAction;
    }

    public DirectAction: DirectAction;
}

export class RouteLinkReply extends ReplyAction {
    public constructor(jsonShape: RouteLinkReply) {
        super();
        this.ReplyActionType = ReplyActionType.RouteLink;
        this.Path = jsonShape.Path;
    }

    public Path: string;
}

export class ServerComponentLinkReply extends ReplyAction {
    public constructor(jsonShape: ServerComponentLinkReply, jsonMapper: JsonEnricher) {
        super();
        this.ReplyActionType = ReplyActionType.ServerComponentLink;
        this.Path = jsonShape.Path;
        this.Title = jsonShape.Title;
        this.Message = jsonShape.Message;
        this.Target = jsonShape.Target;
        this.InjectedClass = jsonShape.InjectedClass;
        this.NextEvent = hydrateNextEvent(jsonShape.NextEvent, jsonMapper);
    }

    public Path: string;
    public Title: string;
    public Message: string;
    public Target: GenericSelectorTargets;
    public InjectedClass: string;
}

export class ChainedInteractionReply extends ReplyAction {
    public constructor(jsonShape: ChainedInteractionReply, jsonMapper: JsonEnricher) {
        super();
        this.ReplyActionType = ReplyActionType.ChainedInteraction;
        this.NextEvent = hydrateNextEvent(jsonShape.NextEvent, jsonMapper);
    }
}

// Shared by ServerComponentLinkReply/ChainedInteractionReply — a NextEvent is either a
// plain Interaction or a QuestStatus (the only two InteractionType discriminators a
// NextEvent can carry today).
function hydrateNextEvent(nextEvent: Interaction, jsonMapper: JsonEnricher): Interaction {
    if (!nextEvent) return null;
    if (nextEvent.InteractionType == InteractionType.Quest)
        return new QuestStatus(<QuestStatus>nextEvent, jsonMapper);
    return new Interaction(nextEvent, jsonMapper);
}

// Shared discriminator dispatch for a raw ReplyAction, mirroring hydrateNode().
export function hydrateReplyAction(jsonShape: ReplyAction, jsonMapper: JsonEnricher): ReplyAction {
    if (!jsonShape) return null;
    switch (jsonShape.ReplyActionType) {
        case ReplyActionType.DirectAction: return new DirectActionReply(<DirectActionReply>jsonShape);
        case ReplyActionType.RouteLink: return new RouteLinkReply(<RouteLinkReply>jsonShape);
        case ReplyActionType.ServerComponentLink: return new ServerComponentLinkReply(<ServerComponentLinkReply>jsonShape, jsonMapper);
        case ReplyActionType.ChainedInteraction: return new ChainedInteractionReply(<ChainedInteractionReply>jsonShape, jsonMapper);
        default: return null;
    }
}

export class DirectAction {
    public ExternalId: string;
    public RecipientType: DirectActionRecipientType;
    public ActionCode: number;
}

export class DisplayMetadata {
    public IsDangerOption: boolean;
    public IsPrimaryOption: boolean;
    public IsFinalOption: boolean;
}

export class Signal {
    public SignalType: SignalType;

    public AlertText: string;

    public constructor(jsonShape: Signal) {
        this.AlertText = jsonShape.AlertText;
        this.SignalType = jsonShape.SignalType;
    }
}

export class NotificationSignal extends Signal {
    public Notification: Notification;
    public constructor(jsonShape: NotificationSignal, myUsername: string, jsonMapperService: JsonEnricher) {
        super(jsonShape);
        if (!jsonShape) { return; }
        this.Notification = new Notification(jsonShape.Notification, myUsername, jsonMapperService);
    }
}

export class Notification {
    Id: string;
    Entity: Friendship | Message;
    NotificationType: NotificationType;
    CreateDate: Date;
    SendDate: Date;
    ReadDate: Date;

    public constructor(jsonShape: Notification, myUsername: string, jsonMapperService: JsonEnricher) {
        if (!jsonShape) { return; }
        this.Id = jsonShape.Id;
        this.NotificationType = jsonShape.NotificationType;
        this.CreateDate = new Date(jsonShape.CreateDate);
        this.SendDate = new Date(jsonShape.SendDate);
        if (jsonShape.ReadDate) {
            this.ReadDate = new Date(jsonShape.ReadDate);
        }

        switch (jsonShape.NotificationType) {
            case NotificationType.FriendshipRequest: this.Entity = new Friendship().map(<Friendship>jsonShape.Entity, jsonMapperService); break;
            case NotificationType.PrivateMessage: this.Entity = new Message(<Message>jsonShape.Entity, myUsername); break;
            case NotificationType.MessageBoard: this.Entity = new Message(<Message>jsonShape.Entity, myUsername); break;
            default: this.Entity = null;

        }
    }
}

export class RandomEventNotification extends Signal {
    constructor(jsonShape: RandomEventNotification, jsonMapper: JsonEnricher) {
        super(jsonShape);
        if (!jsonShape) { return; }
        if (jsonShape.PlayerStateUpdate)
            this.PlayerStateUpdate = new PlayerStateUpdate(jsonShape.PlayerStateUpdate, jsonMapper);

        this.ImagePath = jsonShape.ImagePath;
        this.Id = jsonShape.Id;
        this.Title = jsonShape.Title;
    }
    // Id doubles as the system-notification queue row's ack token, whether this instance came
    // from the live SignalR push (RandomEvent.Id, set at the top level) or from a fetched
    // SystemNotificationDTO's Content (where Id lives on the wrapper - see SystemNotificationService,
    // which copies it across after construction).
    public Id: string;
    public Title: string;
    public PlayerStateUpdate: PlayerStateUpdate;
    public ImagePath: string;
}

// SystemNotificationType is a closed enum today (RandomEvent only) but is meant to grow - see
// Docs/CritterServer/System-Notifications-Architecture.md. Mirrors the server's enum ordering.
export enum SystemNotificationType {
    RandomEvent = 0
}

// One row from GET api/systemNotifications - the durable-queue shape, distinct from the live
// SignalR push (RandomEvent). Content is only ever a RandomEventNotification today; a future
// SystemNotificationType would need its own case here.
export class SystemNotification {
    constructor(jsonShape: SystemNotification, jsonMapper: JsonEnricher) {
        if (!jsonShape) { return; }
        this.Id = jsonShape.Id;
        this.Type = jsonShape.Type;
        this.CreateDate = jsonShape.CreateDate;
        this.Tries = jsonShape.Tries;
        this.ExpiryDate = jsonShape.ExpiryDate;

        this.Content = new RandomEventNotification(<RandomEventNotification>(<unknown>jsonShape.Content), jsonMapper);
        this.Content.Id = jsonShape.Id; // Content itself carries no id - only the wrapper does
    }

    public Id: string;
    public Type: SystemNotificationType;
    public Content: RandomEventNotification;
    public CreateDate: string;
    public Tries: number;
    public ExpiryDate: string;
}

export class PlayerStateUpdate {
    constructor(jsonShape: PlayerStateUpdate, jsonMapper: JsonEnricher) {
        if (!jsonShape) { return; }
        this.CashChangedAmount = jsonShape.CashChangedAmount;
        if (jsonShape.AddedItems)
            this.AddedItems = jsonShape.AddedItems.map(ai => jsonMapper.map(ai, InventoryItem));
        if (jsonShape.RemovedItems)
            this.RemovedItems = jsonShape.RemovedItems.map(ri => jsonMapper.map(ri, InventoryItem));
    }

    public AddedItems: InventoryItem[];
    public RemovedItems: InventoryItem[];
    public CashChangedAmount: number | null;

    // The one place a PlayerStateUpdate delta gets patched into the client's inventory/cash caches -
    // every path that applies one (GameStateResponse, live random-event pushes) should call this
    // rather than hand-rolling it.
    public static distributeToServices(update: PlayerStateUpdate, inventoryService: InventoryService, userService: UserService): void {
        if (!update) { return; }
        inventoryService.insertItems(...update.AddedItems);
        inventoryService.removeItems(...update.RemovedItems);
        userService.updateUserCash(update.CashChangedAmount);
    }
}

export class AchievementAlert extends Signal {
    public Achievement: AchievementConfig;
}

export class GameConfig {
    private static ASSET_LOCATION_PREFIX = 'assets/images/tiles/games/';
    public Id: GameType;
    public Name: string;
    public Description: string;
    public ImagePath: string;
    public constructor(jsonShape: GameConfig) {
        if (!jsonShape) return;
        this.Id = jsonShape.Id;
        this.Name = jsonShape.Name;
        this.Description = jsonShape.Description;
        if (!jsonShape.ImagePath.includes(GameConfig.ASSET_LOCATION_PREFIX)) {
            this.ImagePath = GameConfig.ASSET_LOCATION_PREFIX + jsonShape.ImagePath;
        } else {
            this.ImagePath = jsonShape.ImagePath;
        }
    }
}

export class LeaderboardEntryDTO {
    public Username: string;
    public GameId: GameType;
    public SubmitDate: Date;
    public Score: number;
}

export class UserUpload {
    Id: number;
    Username: string;
    SubmitDate: Date;
    Filepath: string;
}



export class StaticContent {

    constructor(jsonShape: StaticContent) {
        this.Id = jsonShape.Id;
        this.ContentType = jsonShape.ContentType;
        if (this.PublishDate)
            this.PublishDate = new Date(jsonShape.PublishDate);
        this.ImagePath = jsonShape.ImagePath;
        this.Title = jsonShape.Title;
        this.Message = jsonShape.Message;
        this.MessageHtml = jsonShape.MessageHtml;
        this.AlertTitle = jsonShape.AlertTitle;
        this.AlertMessage = jsonShape.AlertMessage;
        this.AlertCssClasses = jsonShape.AlertCssClasses;
        this.AlertIconCssClasses = jsonShape.AlertIconCssClasses;
        this.AlertScrollTargetId = jsonShape.AlertScrollTargetId;
        this.AlertDismissable = jsonShape.AlertDismissable;
        this.ContentFilePath = jsonShape.ContentFilePath;
        this.RouterLink = jsonShape.RouterLink;
        this.Tags = jsonShape.Tags;
        if (this.VisibleStartDate)
            this.VisibleStartDate = new Date(jsonShape.VisibleStartDate);
        if (this.VisibleEndDate)
            this.VisibleEndDate = new Date(jsonShape.VisibleEndDate);
        this.Username = jsonShape.Username;
    }

    Id: number;
    ContentType: ContentType;
    PublishDate: Date;
    ImagePath: string;
    Title: string;
    Message: string;
    MessageHtml: string;
    AlertTitle: string;
    AlertMessage: string;
    AlertCssClasses: string;
    AlertIconCssClasses: string;
    AlertScrollTargetId: string;
    AlertDismissable: boolean;
    ContentFilePath: string;
    RouterLink: string;
    Tags: string[];
    VisibleStartDate: Date;
    VisibleEndDate: Date;
    Username: string;
}


export class MultiplayerGameSummary {
    Id: string;
    Players: User[];
    GameType: GameType;
    GameOver: boolean;
    Host: User;
}

export class GameScoreResult {
    CashWon: number;
    RemainingSubmissions: number;
}

export class AchievementConfig {
    public Id: number;
    public Name: string;
    public Description: string;
    public ImagePath: string;

    private static ASSET_LOCATION_PREFIX: string = 'assets/images/achievements/';
    public constructor(jsonShape: AchievementConfig) {
        if (!jsonShape) return;
        this.Id = jsonShape.Id;
        this.Name = jsonShape.Name;
        this.Description = jsonShape.Description;
        if (jsonShape.ImagePath && !jsonShape.ImagePath.includes(AchievementConfig.ASSET_LOCATION_PREFIX)) {
            this.ImagePath = AchievementConfig.ASSET_LOCATION_PREFIX + jsonShape.ImagePath;
        } else {
            this.ImagePath = jsonShape.ImagePath;
        }
    }
}

export class UserArgumentBag {
    ItemIds: number[] = [];
    ShopIds: number[] = [];
}

// Mirrors CritterServer.Contract/Spotlight/SpotlightImageType.cs - ordinal values must match exactly.
export enum SpotlightImageType {
    UserAvatar,
    PumpkinSubmission,
    GameIcon
}

// Mirrors CritterServer.Contract/Spotlight/SpotlightNavigationType.cs - ordinal values must match exactly.
export enum SpotlightNavigationType {
    None,
    Profile,
    Game,
    Contest,
    PumpkinSubmission,
    Shop
}

export interface SpotlightImageReference {
    Type: SpotlightImageType;
    TargetId: string;
}

export interface SpotlightNavigation {
    Type: SpotlightNavigationType;
    TargetId: string;
}

export class SpotlightEvent implements JsonMappable<SpotlightEvent> {
    repositoryLabel = 'SpotlightEvent';
    isJsonMappableInstance = true;

    map(jsonShape: SpotlightEvent, jsonMapper: JsonEnricher): SpotlightEvent {
        if (!jsonShape) return null;

        this.Id = jsonShape.Id;
        this.Kind = jsonShape.Kind;
        this.Title = jsonShape.Title;
        this.Subtitle = jsonShape.Subtitle;
        this.Image = jsonShape.Image;
        this.Navigation = jsonShape.Navigation;
        this.Payload = jsonShape.Payload;
        this.CreatedUtc = jsonShape.CreatedUtc;
        return this;
    }

    cacheNestedElements(unifiedRepoGateway: UnifiedRepositoryGatewayService) { /*nothing nested :) */ }

    public Id: string;
    public Kind: string;
    public Title: string;
    public Subtitle: string;
    public Image: SpotlightImageReference;
    public Navigation: SpotlightNavigation;
    public Payload: unknown;
    public CreatedUtc: string;
}

// Pushed over SignalR (SignalType.Spotlight) whenever a new SpotlightEvent is created - see SpotlightSignal.cs.
export class SpotlightSignal extends Signal {
    public SpotlightEvent: SpotlightEvent;

    public constructor(jsonShape: SpotlightSignal) {
        super(jsonShape);
        this.SpotlightEvent = new SpotlightEvent().map(jsonShape.SpotlightEvent, null);
    }
}
